import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { getHowSellIBM } from '@shared-logic/orders';
import { Order, orderOriginConst, OrderExchange } from '@shared-logic/orders';
import { User } from '@shared-logic/user';
//import { IndicatorIBM /*, PxQIBMString*/ } from '@shared-logic/integration-ibm/interfaces/responseIBM.interface';
import { getCookie } from '../session.checker';
import { ResponseRequest } from '@shared-logic/shared-models';
import { getPurchaseMethodIBM } from '@shared-logic/purchase-method';
import {
  classifyPurchaseMethodDefault, classifyPurchaseMethodValidation, IbmCallOutcome,
  PurchaseMethodDefaultClassification, PurchaseMethodValidationResult
} from '@shared-logic/integration-ibm';

interface ReqVALICUITCV {
  VEND: number;
}

interface ReqCUPOSOV {
  CONT: number;
  FECH: number;
  CAM: number;
  ESTA: string;
}

interface ReqVALIRUSI {
  VEND: number;
  PROD: string;
}

interface ReqVALCATE {
  VEND: number;
  COMOV: string;
  CORR: number;
}

interface ReqVALCOMO {
  COMP: number;
  COMO: string;
}

interface ReqCOMODEF {
  COMP: number;
}

export interface CreateOrderWithExchangeRequest {
  order: Order;
  exchange: OrderExchange;
}

/* Transport-level failure of an IBM validation request: the response never carried
a usable business answer. It exposes the HTTP status and, when the error body is
readable JSON, its payload, so callers can tell a degraded dependency apart from a
business rejection instead of reading an error body as if it were a valid answer. */
export class IbmRequestError extends Error {
  constructor(
    public readonly funcion: string,
    public readonly status: number,
    public readonly body: unknown | null
  ) {
    super('IBM request ' + funcion + ' failed with HTTP status ' + status);
    this.name = 'IbmRequestError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  constructor(private http: HttpClient) { }

  /*getOrdersDashboard(email: String, status: any) {
    const request = { email: email, status: status.toString() }
    return this.http.post<Order[]>(environment.environment.rutas.dashboard.getOrders, request);
    //We deliver the orders of the Dashboard
  }*/

  getAccumulatedTonnage(productName: number, harvest: string) {
    return this.http.get<any>(
      environment.rutas.dashboard.getAccumulatedTonnage + `/${productName}/${harvest}`
    );
  }

  getOrdersToday() {
    return this.http.get<ResponseRequest>(environment.rutas.orders.getOrdersToday);
  }

  getOrdersByDate(date: any) {
    return this.http.get<ResponseRequest>(
      environment.rutas.orders.getOrdersByDate + `?startDate=${date}`
    );
  }

  getOrdersByRange(date1: string, date2: string, statusOrder: number = 0) {
    return this.http.get<ResponseRequest>(
      environment.rutas.orders.getOrdersByRange +
      `?startDate=${date1}&endDate=${date2}` +
      (statusOrder != 0 ? `&status=${statusOrder}` : '')
    );
  }

  getOrderByID(id: any) {
    return this.http.get<ResponseRequest>(environment.rutas.orders.getOrderByID + `/${id}`);
    //We get the order by ID
  }

  getOrderByContract(contract: number) {
    return this.http.get<ResponseRequest>(
      environment.rutas.orders.getOrderByContract + `?contractNumber=${contract}`
    );
  }

  createOrder(order: Order) {
    let newOrder = JSON.parse(JSON.stringify(order));
    delete newOrder._id;
    delete newOrder.created_at;
    delete newOrder.price._id;

    return this.http.post<ResponseRequest>(
      environment.rutas.orders.createOrder,
      JSON.stringify(newOrder)
    );
    //We create the order
  }

  createOrderWithExchange(data: CreateOrderWithExchangeRequest[]) {
    return this.http.post<ResponseRequest>(
      environment.rutas.orders.createOrderWithExchange,
      data
    );
  }

  /*----------------------------------------------*/
  //We edit the order
  editOrderNumber(id: any, fields: string, value: any, user: User) {
    const request = {
      campo: fields,
      valorI: value,
      name: user.name,
      lastName: user.lastName,
    };

    return this.http.put<ResponseRequest>(
      environment.rutas.orders.editOrderNumber + `/${id}`,
      JSON.stringify(request)
    );
  }

  //We edit the order
  updateOrder(order: Order, id: string) {
    const isManual =
      order.orderDestination.orderOrigin == orderOriginConst.manual;
    let request = JSON.parse(JSON.stringify(order));
    delete request._id;
    if (isManual) {
      delete request.price._id;
    }

    return this.http.post<ResponseRequest>(
      environment.rutas.orders.updateOrder + '/' + id,
      request
    );
  }

  //---------- HISTORICAL ----------------\\
  getHistorical(id: string) {
    return this.http.get<ResponseRequest>(
      environment.rutas.orders.historical.replace('{id}', id)
    );
  }

  //--------------- VERSIONS ----------------\\
  getFirstVersion(id: string) {
    return this.http.get<ResponseRequest>(
      environment.rutas.orders.getFirstVersion + `/${id}`
    );
  }

  // ---------- VALIDATIONS IBM ----------------\\

  // ------------ CURL EXAMPLE ----------------\\
  /*curl -i -X POST -H "Authorization:Bearer d69x26XvDl4xsMXCG3nywuPbDbnr8r"    
  -H "Content-Type:application/x-www-form-urlencoded"    
  -d "funcion=VALICUITCV" -d "ambiente=desarrollo" -d 
  "accion=_VEND_4486__VEND_" 'https://api.grassisa.com.ar/api/v2/IBM/Mediador/WakeUp/'*/

  //------------  VALIDATION ----------------\\
  async getValidationForSeller(codeS: number) {
    return this.sendRequest('VALICUITCV', { VEND: codeS });
  }

  async getValidateRucaAndSisa(codeS: number, howSell: number) {
    return this.sendRequest('VALIRUSI', {
      VEND: codeS,
      PROD: getHowSellIBM(howSell),
    });
  }

  async getValidateHowSell(codeS: number, howSell: number, broker: number) {
    return this.sendRequest('VALCATE', {
      VEND: codeS,
      COMOV: getHowSellIBM(howSell),
      CORR: broker,
    });
  }

  /* Returns the classified verdict instead of the raw envelope: callers must not have
  to tell a business rejection apart from an outage by inspecting the payload, which is
  how a server error used to be reported as an invalid purchase method. */
  async getValidatePurchaseMethod(buyerCode: number, purchaseMethod: number): Promise<PurchaseMethodValidationResult> {
    const outcome = await this.settleIbmCall('VALCOMO', {
      COMP: buyerCode,
      COMO: getPurchaseMethodIBM(purchaseMethod),
    });

    return classifyPurchaseMethodValidation(outcome);
  }

  // Per-buyer Purchase Method default (IBM function COMODEF, program WEB0053).
  // Same direct frontend -> be-ibm pattern as VALCOMO above.
  /* Also returns a classified result, so an absent default ('Sin Valor') stays a
  successful answer while an unreachable lookup is reported as unavailable. */
  async getDefaultPurchaseMethod(buyerCode: number): Promise<PurchaseMethodDefaultClassification> {
    const outcome = await this.settleIbmCall('COMODEF', {
      COMP: buyerCode,
    });

    return classifyPurchaseMethodDefault(outcome);
  }

  /* Turns the two ways an IBM call can end -- a parsed body, or a rejection from a
  non-2xx status or a network failure -- into one value the classifier can read, so no
  caller has to wrap the call in its own try/catch to stay correct. */
  private async settleIbmCall(
    funcion: string,
    value: ReqVALCOMO | ReqCOMODEF
  ): Promise<IbmCallOutcome> {
    try {
      return { settled: 'body', body: await this.sendRequest(funcion, value) };
    } catch (error) {
      return { settled: 'failure', error };
    }
  }

  /*async insertNewIndicator(indicator: IndicatorIBM) {
    return this.sendRequest('ALTAFIOV', indicator);
  }*/

  /*async solicitateQuotas(
    contractNumber: number,
    date: number,
    quantTrucks: number,
    AssignmentType: string
  ) {
    return this.sendRequest('CUPOSOV', {
      CONT: contractNumber,
      FECH: date,
      CAM: quantTrucks,
      ESTA: AssignmentType,
    });
  }*/

  /*async getValidatePxQ(PxQ: PxQIBMString){
    return this.sendRequest('VALIDAPXQ', PxQ);
  }*/


  async sendRequest(funcion: string, value: ReqCUPOSOV | ReqVALCATE | ReqVALICUITCV | ReqVALIRUSI | ReqVALCOMO | ReqCOMODEF /* | IndicatorIBM /*| PxQIBMString*/): Promise<any> {

    const response = await fetch(environment.rutas.ibm.validation + '?funcion=' + funcion,
      {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + getCookie('refresh_token'),
        },
        body: JSON.stringify(value)
      });

    /* A non-2xx response may still carry a JSON body shaped like a business answer.
    Parsing it as success would report an outage as a rule violation, so the status
    is checked before the body is ever handed back. */
    if (!response.ok) {
      throw new IbmRequestError(funcion, response.status, await this.readJsonBodyOrNull(response));
    }

    return response.json();
  }

  /* An error response may carry JSON, plain text or nothing at all. None of those
  cases may mask the HTTP status, so a body that cannot be parsed is reported as
  absent rather than throwing over the original failure. */
  private async readJsonBodyOrNull(response: Response): Promise<unknown | null> {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
}
